// Deal slide-over — right-hand detail panel for one trades-log row.
//
// GET /api/trades has no per-deal detail endpoint, so the panel renders from
// the list-row data alone. Manager's-cheque deals get enriched via the
// existing GET /api/trades/mc/:dealId (api.getMcDeal) when it resolves;
// failures fall back silently to the row data. An AWAITING trade is enriched a
// second way — from the settle-later read, which is the only endpoint that
// knows why a leg is still open and whether one closed onto a party's running
// account.
//
// HOW A SETTLED TRADE CLOSED (sweep §5.19, 14 Sept 2026): the sweep writes it
// into deals.notes ("settled by sweep 2026-09-12 (USDT netted · INR netted)",
// "on account — 20,807 INR rode"). The trades list route selects notes since
// item 59 (14 Sept 2026), so this panel prints it as a "Closed" row; a row
// from an older Worker carries none and says plain "Settled". Guessing from
// the party's setting would be worse: it would label trades that were paid in
// full.
//
// Minimised 14 Sept 2026 (sweep §5.19 / item 59): the legs read "Customer pays
// / Customer receives" (Book trade's words), Delivery + Payment method are one
// "Via" row hidden when it would say "Other", the timeline only shows for a
// cheque or a void (for every other deal it repeated the Booked rows), and the
// footer lost "Close" (the X and Escape exist). The void panel opens above the
// footer, full width, with one red button at a time. One KeyValue, one Pill —
// the shared pieces — instead of this file's own KV and console pill.
//
// Void lives here (added 2026-08-22). Until then the only void endpoint was
// MC-specific, so correcting an ordinary mistyped trade meant reverting ledger
// legs by hand — which is how a misnamed counterparty stayed on the books for
// twenty minutes and briefly got double-booked during the fix. An operator who
// can book from the console has to be able to unbook from it.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { api, type TradeRow } from '../lib/api';
import { displayAsset, fmtAmount, fmtMoney, fmtDate, fmtRelative, tidySlug, isoToDubaiWall, fmtRateExact } from '../lib/format';
import { spreadAsset } from '../lib/workflow';
import { SettleButton, legWords } from './SettleButton';
import { partyName, rodeSentence } from '../lib/naming';
import { errorWords, ApiError } from '../lib/errors';
import { Pending } from './Pending';
import { Pill, type PillTone } from './Pill';
import { KeyValue, KeyValueRow } from './KeyValue';
import { CopyRef } from './CopyRef';
import { ErrorLine, WarnLine } from './ErrorLine';

// Row shape lives in lib/api.ts next to DealRow; re-exported here so the
// slide-over's consumers keep a single import site for row + label helpers.
export type { TradeRow } from '../lib/api';

export type TradeKind = 'buy' | 'sell' | 'mc';

/** IS THIS A CHEQUE DEAL. One rule for every screen (19 Sept 2026, the
 *  operator on Home: "wk line reads wrong" — WK May's manager cheque wore a
 *  Buy pill and read "pays 2,49,996 AED · receives 93,53,840 AED @ 1"). The
 *  books mark a cheque three ways depending on which door booked it: the
 *  deal id's MC- prefix, `delivery_method` manager_cheque/passthrough, or
 *  `payment_method` manager-cheque — this reads all three. */
export function isChequeDeal(d: { deal_id?: string | null; delivery_method?: string | null; payment_method?: string | null }): boolean {
  if (d.deal_id && d.deal_id.startsWith('MC-')) return true;
  if (d.delivery_method === 'manager_cheque' || d.delivery_method === 'passthrough') return true;
  const pm = (d.payment_method ?? '').replace('_', '-');
  return pm === 'manager-cheque';
}

/** Buy/Sell from the customer's perspective; cheque deals are MC. */
export function tradeKind(t: TradeRow): TradeKind {
  if (isChequeDeal(t)) return 'mc';
  return t.direction === 'customer_buy' ? 'buy' : 'sell';
}

export const KIND_LABEL: Record<TradeKind, string> = { buy: 'Buy', sell: 'Sell', mc: 'MC' };
/** No colour on a direction pill (sweep item 72) — the word carries it. Gold
 *  is the cheque's kind pill, the one tone Pill.tsx reserves for it. */
export const KIND_TONE: Record<TradeKind, PillTone> = { buy: 'neutral', sell: 'neutral', mc: 'gold' };

/** Delivery and payment methods in words. Both spellings of each token are
 *  listed on purpose: admin.deals holds `manager-cheque` on the payment_method
 *  column and `manager_cheque` on delivery_method, and the dashed one used to
 *  fall through to tidySlug and print "Manager Cheque" beside the underscored
 *  one's "Manager's cheque". */
export function deliveryLabel(m: string | null | undefined): string {
  switch (m) {
    case 'cash':           return 'Cash';
    case 'bank_transfer':
    case 'bank-transfer':  return 'Bank transfer';
    case 'crypto':         return 'Crypto';
    case 'manager_cheque':
    case 'manager-cheque': return "Manager's cheque";
    case 'passthrough':    return 'Passthrough';
    default:               return m ? tidySlug(m) : '—';
  }
}

/** ONE "VIA" ROW (sweep §5.19): delivery or payment method, whichever is
 *  said, hidden when it would only say "Other" (52 of 274) or nothing (145
 *  null), and said once when both are the same word (both cash printed Cash
 *  twice). Null means the row is not rendered. */
export function viaWords(t: TradeRow): string | null {
  const words = [t.delivery_method, t.payment_method]
    .filter((m): m is string => !!m && m.toLowerCase() !== 'other')
    .map(deliveryLabel);
  const unique = [...new Set(words)];
  return unique.length > 0 ? unique.join(' · ') : null;
}

export function lifecycleLabel(l: string | null | undefined): string {
  switch (l) {
    case 'settled':           return 'Settled';
    case 'quoted':            return 'Quoted';
    case 'awaiting':          return 'Awaiting';
    // "Carried in" everywhere (the party page already said it; the log and
    // this panel said "Carried into opening" — one state, one word, 14 Sept).
    case 'absorbed':          return 'Carried in';
    case 'voided':            return 'Void';
    case 'mc_pending_seller': return 'Awaiting seller';
    case 'mc_seller_paid':    return 'Seller paid';
    default:                  return l ? tidySlug(l) : '—';
  }
}

export function lifecycleTone(l: string | null | undefined): PillTone {
  switch (l) {
    case 'settled':           return 'good';
    case 'quoted':            return 'info';
    case 'awaiting':
    case 'mc_pending_seller':
    case 'mc_seller_paid':    return 'warn';
    case 'voided':            return 'bad';
    default:                  return 'neutral';
  }
}

/** 'tg:12345' → 'Operator 12345'; anything else passes through. */
export function operatorLabel(bookedBy: string | null | undefined): string {
  if (!bookedBy) return '—';
  const m = /^tg:(\d+)$/.exec(bookedBy);
  return m ? `Operator ${m[1]}` : bookedBy;
}

/** WHY A TRADE IS VOIDED, AS CHIPS (sweep item 32, 14 Sept 2026). The route
 *  took four characters, and "wrong" is a real reason in the books — which
 *  six months on says nothing. A chip and free text count together, ten
 *  characters at least; the chip alone is enough only when it says enough. */
export const VOID_REASONS = ['Duplicate', 'Wrong party', 'Wrong amount', 'Wrong direction', 'Test'] as const;
export const VOID_REASON_MIN = 10;

/** THE SAME TRADE THE OTHER WAY ROUND (sweep item 32 / 3.1 #1). A trade booked
 *  on the wrong side is voided and booked again — this link opens Book trade
 *  with the party, both amounts, the rate and the time carried over and the
 *  direction flipped, so nothing is retyped and nothing is swapped by hand.
 *  The customer now pays what they received and receives what they paid.
 *  Book trade seats ?take= / ?give= by the asset of each side's default rail,
 *  so ?takeAsset= / ?giveAsset= ride along for it to honour; ?when= is the
 *  Dubai wall-clock of the original booking. Never automatic: it is a link,
 *  and the readback sentence is its title. */
export function flippedTrade(t: TradeRow): { href: string; words: string } {
  const dir  = t.direction === 'customer_buy' ? 'customer_sell' : 'customer_buy';
  const who  = partyName({ display_name: t.party_name, slug: t.party_slug });
  const pays = { asset: t.asset_give, amount: String(t.amount_give) };   // what they received, now paid
  const gets = { asset: t.asset_take, amount: String(t.amount_take) };   // what they paid, now received
  const q = new URLSearchParams({
    party: t.party_slug, dir,
    take: pays.amount, give: gets.amount,
    takeAsset: pays.asset, giveAsset: gets.asset,
  });
  if (t.rate != null && String(t.rate) !== '') q.set('rate', String(t.rate));
  const when = isoToDubaiWall(t.booked_at);
  if (when) q.set('when', when);
  q.set('note', `booked again the other way from ${t.deal_id}`);
  return {
    href: `/trade?${q.toString()}`,
    words: `${who} pays ${fmtAmount(pays.amount, pays.asset)} ${displayAsset(pays.asset)} and receives ${fmtAmount(gets.amount, gets.asset)} ${displayAsset(gets.asset)}`,
  };
}

/** A failed read, as the ONE red line every screen draws (role="alert"): what failed, Try again, the server's own sentence behind
 *  "Details" (22 Oct 2026 sweep — it was the whole sentence, 25 words, in a 440 px panel; Astra: it was amber, without the alert).
 *  The kit's StateLine does this for the screens; this file cannot import the kit (kit → Ledger → this file), so the line is
 *  drawn here, the same way. */
function FailedRead({ what, error, onRetry, busy }: { what: string; error: unknown; onRetry: () => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <span role="alert" className="block text-neg">
      Couldn&rsquo;t read {what}.{' '}
      <button type="button" aria-expanded={open} className="text-meta text-tg-hint underline decoration-dotted hover:text-tg-text" onClick={() => setOpen((v) => !v)}>
        {open ? 'Less' : 'Details'}
      </button>{' '}
      <button type="button" className="btn-link text-body" disabled={busy} onClick={onRetry}>{busy ? 'Trying…' : 'Try again'}</button>
      {open && <span className="mt-1 block text-meta text-tg-hint">{errorWords(error)}</span>}
    </span>
  );
}

// ── slide-over ───────────────────────────────────────────────────────────────

type TlState = 'done' | 'wait' | 'off';
const TL_DOT: Record<TlState, string> = { done: 'bg-pos', wait: 'bg-warn', off: 'bg-tg-hint' };

const MONO = 'amt';

export function DealSlideOver({ deal, onClose }: { deal: TradeRow | null; onClose: () => void }) {
  const open = deal !== null;

  // Retain the last row so content stays visible through the close transition.
  const [shown, setShown] = useState<TradeRow | null>(deal);
  useEffect(() => { if (deal) setShown(deal); }, [deal]);

  const qc = useQueryClient();
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [voidChip, setVoidChip]       = useState<(typeof VOID_REASONS)[number] | null>(null);
  const [voidReason, setVoidReason]   = useState('');
  const [voiding, setVoiding]         = useState(false);
  const [voidError, setVoidError]     = useState<string | null>(null);
  const [voidBlocked, setVoidBlocked] = useState(false);   // the plain void refused: newer trades on the book
  // The chip and the free text together are the reason the books keep.
  const voidWords = [voidChip, voidReason.trim()].filter(Boolean).join(' — ');
  const reasonOk  = voidWords.length >= VOID_REASON_MIN;
  // A confirm left open behind a closed panel would fire against whatever deal
  // is opened next. Reset with the row.
  useEffect(() => {
    setConfirmVoid(false); setVoidChip(null); setVoidReason(''); setVoidError(null); setVoidBlocked(false);
  }, [deal?.deal_id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const isMc = !!shown && isChequeDeal(shown);
  const mc = useQuery({
    queryKey: ['mc', 'deal', shown?.deal_id],
    queryFn:  () => api.getMcDeal(shown!.deal_id),
    enabled:  open && isMc,
    retry:    false,
    staleTime: 60_000,
  });
  const md = mc.data?.deal;

  const kind = shown ? tradeKind(shown) : null;
  // Same grammar as the log behind it — the panel is the same party, so it
  // must not spell the name differently. No relevance filtering anywhere in
  // this component: a trade is evidence, and evidence is never hidden.
  const party    = shown ? partyName({ display_name: shown.party_name, slug: shown.party_slug }) : '';
  const merchant = shown && (shown.merchant_name || shown.merchant_slug)
    ? partyName({ display_name: shown.merchant_name, slug: shown.merchant_slug })
    : '';
  // Match TradesLog: MC *and* passthrough have no implicit settled state.
  const lifecycle = md?.lifecycle ?? shown?.lifecycle ?? (kind === 'mc' ? null : 'settled');
  // How a settled trade closed — the list route's `notes` (see the header).
  const closed = shown && lifecycle === 'settled' ? (shown.notes ?? null) : null;

  // ── WHY THIS TRADE IS STILL OPEN, AND HOW IT CLOSES ──────────────────────
  //
  // The panel used to show an awaiting trade with a Settle button and nothing
  // else: the operator had to leave for /settle to learn which leg was short.
  // This is the same read /settle and the party page make, cached under the
  // same key, so the three cannot disagree — and it is where this panel can say
  // that a leg closed onto a running account with the rest riding, in the same
  // words as everywhere else.
  //
  // Only for an awaiting trade. A settled row carries no leg verdicts anywhere
  // the console can reach, so the panel says nothing about one rather than
  // guessing.
  const settle = useQuery({
    queryKey: ['settlements', 'dubaiotc'],
    queryFn:  () => api.settlements('dubaiotc'),
    enabled:  open && lifecycle === 'awaiting',
    retry:    false,
    staleTime: 15_000,
  });
  const legs = settle.data?.parties
    .flatMap((p) => p.deals)
    .find((d) => d.deal_id === shown?.deal_id)?.legs ?? null;
  // The sweep runs on that read, so it may have closed THIS trade a moment ago
  // — in which case it has left `parties` and the row behind this panel is
  // already stale. `swept` is the only place that says so, and the only place
  // that carries what rode.
  const sweptNow = settle.data?.swept.find((s) => s.deal_id === shown?.deal_id) ?? null;

  // Timeline — assembled newest-first from data already on hand (row + MC
  // enrichment); no extra fetches. ONLY FOR A CHEQUE OR A VOID (§5.19): with
  // one entry it duplicated the Booked rows, which is the norm for every
  // ordinary deal.
  const timeline: Array<{ key: string; time: string; text: string; state: TlState }> = [];
  if (shown) {
    if (lifecycle === 'voided') {
      timeline.push({
        key: 'void', time: '—', state: 'off',
        text: md?.void_reason ? `Deal voided · ${md.void_reason}` : 'Deal voided',
      });
    }
    if (isMc && md) {
      if (md.mc_delivered_at) {
        timeline.push({ key: 'delivered', time: fmtDate(md.mc_delivered_at), text: 'Cheque delivered', state: 'done' });
      } else if (lifecycle !== 'voided') {
        timeline.push({ key: 'delivered', time: 'Not yet', text: 'Cheque delivery', state: 'wait' });
      }
      if (md.mc_seller_paid_at) {
        timeline.push({ key: 'seller-paid', time: fmtDate(md.mc_seller_paid_at), text: 'Seller paid', state: 'done' });
      } else if (lifecycle !== 'voided') {
        timeline.push({ key: 'seller-paid', time: 'Not yet', text: 'Seller payment', state: 'wait' });
      }
    }
    timeline.push({
      key: 'booked', time: fmtDate(shown.booked_at),
      text: `Deal booked · ${operatorLabel(shown.booked_by_admin)}`, state: 'done',
    });
  }
  const showTimeline = timeline.length > 1;

  const via = shown ? viaWords(shown) : null;
  const flipped = shown ? flippedTrade(shown) : null;

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Deal detail"
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-[440px] sm:max-w-[92vw] bg-tg-section-bg border-l border-tg-secondary shadow-2xl flex flex-col transform transition-transform duration-200 lg:w-[460px] lg:border-stroke lg:shadow-[-18px_0_40px_rgba(0,0,0,0.4)] ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {shown && kind && flipped && (
          <>
            <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-tg-secondary shrink-0 lg:px-[18px] lg:py-4 lg:border-stroke-soft">
              <div className="flex items-baseline gap-2 min-w-0">
                <Pill tone={KIND_TONE[kind]}>{KIND_LABEL[kind]}</Pill>
                <h2 className="font-medium text-body truncate lg:font-semibold" title={shown.party_name || undefined}>{party || '—'}</h2>
                <CopyRef value={shown.deal_id} className="shrink-0 text-meta" />
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded text-tg-hint hover:bg-tg-secondary hover:text-tg-text transition-colors shrink-0 lg:rounded-md"
              >
                <X size={15} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-3 lg:px-[18px] lg:py-[18px]">
              <KeyValue>
                <KeyValueRow
                  label="Party"
                  value={
                    <>
                      <Link to={`/parties/${encodeURIComponent(shown.party_slug)}?entity=dubaiotc`} className="btn-link">
                        {party || '—'}
                      </Link>
                      {/* A delivery charge that belongs to this deal (2 Oct
                          2026): its own entry on its own profit line — the
                          link carries the party and the deal, and changes
                          nothing here. */}
                      <Link
                        to={`/record?mode=charge&party=${encodeURIComponent(shown.party_slug)}&deal=${encodeURIComponent(shown.deal_id)}`}
                        className="block btn-link text-meta"
                        onClick={onClose}
                      >
                        Add delivery charge →
                      </Link>
                    </>
                  }
                />
                <KeyValueRow
                  label="Status"
                  value={
                    <>
                      <Pill tone={lifecycleTone(lifecycle)}>{lifecycleLabel(lifecycle)}</Pill>
                      {lifecycle === 'absorbed' && (
                        <span className="block mt-1 text-meta text-tg-hint">
                          Carried into the 1 Sept opening box — nothing here to reverse.
                        </span>
                      )}
                      {/* A voided trade's most common sequel is the same trade
                          the other way (sweep 3.1 #1) — one link, nothing retyped. */}
                      {lifecycle === 'voided' && !isMc && (
                        <Link to={flipped.href} title={flipped.words} className="block mt-1 btn-link text-meta">
                          Book it again the other way →
                        </Link>
                      )}
                    </>
                  }
                />
                {/* HOW THIS TRADE CLOSES, in the settle screen's own words — the
                    legs, and for a party carrying a running account the leg that
                    closed with the rest riding ("INR on account · 15,705 rode").
                    A failed read says so: silence here would read as a trade with
                    nothing outstanding. */}
                {lifecycle === 'awaiting' && (
                  <KeyValueRow
                    label="Settlement"
                    value={
                      settle.isError ? <FailedRead what="the legs" error={settle.error} busy={settle.isFetching} onRetry={() => settle.refetch()} />
                      : sweptNow ? (
                        <span className="text-pos">
                          Closed by the sweep just now
                          {rodeSentence(sweptNow.rode) ? ` — ${rodeSentence(sweptNow.rode)}` : ''}.
                        </span>
                      )
                      : legs ? (
                        <>
                          <span className={legs.take.settled ? 'text-pos' : 'text-warn'}>{legWords(legs.take, 'take')}</span>
                          <span className="text-tg-hint"> · </span>
                          <span className={legs.give.settled ? 'text-pos' : 'text-warn'}>{legWords(legs.give, 'give')}</span>
                        </>
                      )
                      : <Pending inline what="Reading the legs…" />
                    }
                  />
                )}
                {/* How a settled trade closed — present only once the list
                    route sends notes (see the header). */}
                {closed && <KeyValueRow label="Closed" value={closed} />}
                {/* The two legs are what the panel is for — the answering figures. */}
                <KeyValueRow label="Customer pays"     value={<span className="amt-key">{fmtMoney(shown.amount_take, shown.asset_take)}</span>} />
                <KeyValueRow label="Customer receives" value={<span className="amt-key">{fmtMoney(shown.amount_give, shown.asset_give)}</span>} />
                <KeyValueRow label="Rate" value={shown.rate ? <span className={MONO}>{fmtRateExact(shown.rate)}</span> : null} />
                {shown.spread_amount != null && Number(shown.spread_amount) !== 0 && (
                  <KeyValueRow label="Spread" value={<span className={MONO}>{fmtMoney(shown.spread_amount, spreadAsset(shown))}</span>} />
                )}
                {via && (
                  <KeyValueRow
                    label="Via"
                    value={<>{via}{merchant && <span className="text-tg-hint"> · {merchant}</span>}</>}
                  />
                )}
                {shown.beneficiary && <KeyValueRow label="Beneficiary" value={shown.beneficiary} />}
                <KeyValueRow
                  label="Booked"
                  value={<>{fmtDate(shown.booked_at)} <span className="text-tg-hint">· {fmtRelative(shown.booked_at)}</span></>}
                />
                <KeyValueRow label="Booked by" value={<span className="text-tg-hint">{operatorLabel(shown.booked_by_admin)}</span>} />
              </KeyValue>

              {isMc && md && (
                <div className="pt-4">
                  <h3 className="text-label text-tg-hint uppercase tracking-[0.08em] font-medium mb-2">Manager&rsquo;s cheque</h3>
                  <KeyValue>
                    <KeyValueRow label="Face amount" value={<span className={MONO}>{fmtAmount(md.mc_face_amount, md.asset_take)} {displayAsset(md.asset_take)}</span>} />
                    {md.mc_deposit_amount != null && (
                      <KeyValueRow label="Deposit" value={<span className={MONO}>{fmtAmount(md.mc_deposit_amount, md.asset_take)} {displayAsset(md.asset_take)}</span>} />
                    )}
                    <KeyValueRow
                      label="Seller"
                      value={partyName({
                        display_name: md.mc_seller_display_name ?? shown.merchant_name,
                        slug: md.mc_seller_slug,
                      })}
                    />
                    <KeyValueRow
                      label="Seller paid"
                      value={md.mc_seller_paid_at ? fmtDate(md.mc_seller_paid_at) : <span className="text-tg-hint">not yet</span>}
                    />
                    <KeyValueRow
                      label="Delivered"
                      value={md.mc_delivered_at ? fmtDate(md.mc_delivered_at) : <span className="text-tg-hint">not yet</span>}
                    />
                    {md.void_reason && <KeyValueRow label="Void reason" value={md.void_reason} />}
                    {md.notes && <KeyValueRow label="Notes" value={<span className="whitespace-pre-wrap">{md.notes}</span>} />}
                  </KeyValue>
                </div>
              )}

              {/* Timeline — hairline spine, state dots ringed with the panel
                  colour, mono timestamps. Cheques and voids only. */}
              {showTimeline && (
                <div className="pt-5">
                  <h3 className="text-label uppercase tracking-[0.08em] text-tg-hint font-medium mb-3">Timeline</h3>
                  <ul className="border-l border-stroke pl-3.5">
                    {timeline.map((ev) => (
                      <li key={ev.key} className="relative pl-3.5 pb-3.5 last:pb-0">
                        <span
                          aria-hidden
                          className={`absolute -left-[18px] top-1 h-2 w-2 rounded-full ring-[3px] ring-tg-section-bg ${TL_DOT[ev.state]}`}
                        />
                        <div className={`${MONO} text-meta text-tg-hint`}>{ev.time}</div>
                        <div className="text-meta mt-0.5">{ev.text}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* ── void panel — ABOVE the footer, full width (§5.19) ───────────
                A reason is REQUIRED by the route, and demanding it here rather
                than sending a blank one is the point: an unexplained reversal
                is indistinguishable from a mistake six months later. */}
            {confirmVoid && (
              <div className="shrink-0 border-t border-tg-secondary bg-panel-2/50 px-4 py-3 space-y-2.5 lg:px-[18px] lg:border-stroke-soft">
                <h3 className="font-medium text-body">Void this trade?</h3>
                {/* Sweep, 21 Oct 2026: the two facts, each its own short line (it was one 20-word paragraph). */}
                <div className="space-y-0.5 text-meta text-tg-hint">
                  <p>Reverses every leg; both entries stay on the record.</p>
                  <p>Only the newest trade on a book can be voided.</p>
                </div>
                {/* THE REASON: a chip, free text, or both — ten characters
                    together (sweep item 32). "wrong" alone cannot be sent. */}
                <div className="flex flex-wrap gap-1.5">
                  {VOID_REASONS.map((c) => (
                    <button key={c} type="button"
                      className={`chip ${voidChip === c ? 'chip-on' : ''}`}
                      onClick={() => setVoidChip(voidChip === c ? null : c)}>
                      {c}
                    </button>
                  ))}
                </div>
                <input
                  autoFocus
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder={voidChip === 'Duplicate' ? 'DEAL-2026-09-12-…' : ''}
                  className="input w-full text-field lg:text-body"
                />
                {!reasonOk && <p className="text-meta text-tg-hint">Ten characters at least — chip and words together.</p>}
                {/* BOOKED THE WRONG WAY ROUND: the void's usual sequel is the
                    same trade the other way. The link carries everything;
                    the sentence it will book is its title. */}
                <Link to={flipped.href} title={flipped.words} className="block btn-link text-meta" onClick={onClose}>
                  Book it again the other way →
                </Link>
                <ErrorLine>{voidError}</ErrorLine>
                {/* ONE RED BUTTON AT A TIME. After the LIFO refusal the plain
                    void cannot succeed, so it steps back and only the
                    rebuild is red, with the consequence said above it. */}
                {voidBlocked && (
                  <WarnLine>Rebuilding replays the newer trades without this one.</WarnLine>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-link text-meta"
                    onClick={() => { setConfirmVoid(false); setVoidError(null); setVoidBlocked(false); }}
                  >
                    Cancel
                  </button>
                  {!voidBlocked && (
                    <button
                      type="button"
                      disabled={!reasonOk || voiding}
                      className="text-body py-1.5 px-3 rounded-lg bg-neg text-white disabled:opacity-40"
                      onClick={async () => {
                        setVoiding(true); setVoidError(null);
                        try {
                          await api.voidTrade(shown.deal_id, voidWords);
                          await qc.invalidateQueries({ queryKey: ['trades'] });
                          setConfirmVoid(false); setVoidChip(null); setVoidReason(''); onClose();
                        } catch (e) {
                          // Surface the route's own words — "not_last_event" names
                          // the deal that has to go first, which is the whole
                          // answer, and paraphrasing it would lose that.
                          setVoidError(e instanceof Error ? e.message : 'Void failed');
                          setVoidBlocked(e instanceof ApiError && e.code === 'not_last_event');
                        } finally { setVoiding(false); }
                      }}
                    >
                      {voiding ? 'Voiding…' : 'Void trade'}
                    </button>
                  )}
                  {/* A trade with newer trades after it on the same cost book
                      cannot be popped off the top. The rebuild replays the
                      book without it and re-meters the later sales — the
                      same route a mid-chain void used to need a script for
                      (11 Sept 2026, Loki's duplicate sale). */}
                  {voidBlocked && (
                    <button
                      type="button"
                      disabled={!reasonOk || voiding}
                      className="text-body py-1.5 px-3 rounded-lg bg-neg text-white disabled:opacity-40"
                      onClick={async () => {
                        setVoiding(true); setVoidError(null);
                        try {
                          await api.rebuildV3({ void_deal_ids: [shown.deal_id], void_reason: voidWords, dry_run: false });
                          await qc.invalidateQueries({ queryKey: ['trades'] });
                          await qc.invalidateQueries({ queryKey: ['inventory'] });
                          setConfirmVoid(false); setVoidChip(null); setVoidReason(''); setVoidBlocked(false); onClose();
                        } catch (e) {
                          setVoidError(e instanceof Error ? e.message : 'Rebuild failed');
                        } finally { setVoiding(false); }
                      }}
                    >
                      {voiding ? 'Voiding…' : 'Void and rebuild the book'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* The Mark settled refusal panel is rendered by SettleButton in
                the button's place; `panelClassName` lays it out as a
                full-width line above the buttons (§5.19, 14 Sept 2026). */}
            <footer className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-tg-secondary shrink-0 lg:px-[18px] lg:py-3.5 lg:border-stroke-soft">
              {/* Void sits on the LEFT, apart from the safe buttons, so it is
                  never the thing a thumb lands on by accident.

                  NOT ON A CARRIED-IN TRADE. All 65 pre-cutover trades carry
                  lifecycle 'absorbed': they were posted in books-v2 and their
                  positions live inside the 1 Sept opening boxes, so there are no
                  v3 legs to reverse. The Status row above says so instead. */}
              {lifecycle !== 'voided' && lifecycle !== 'absorbed' && !isMc && !confirmVoid && (
                <button
                  type="button"
                  onClick={() => setConfirmVoid(true)}
                  disabled={voiding}
                  className="text-body py-1.5 px-3 rounded-lg text-neg hover:bg-neg-bg disabled:opacity-50"
                >
                  {voiding ? 'Voiding…' : 'Void'}
                </button>
              )}
              {lifecycle === 'awaiting' && (
                <SettleButton
                  dealId={shown.deal_id}
                  partySlug={shown.party_slug ?? undefined}
                  onDone={onClose}
                  panelClassName="basis-full order-first"
                />
              )}
              <div className="flex-1" />
              <Link
                to={`/parties/${encodeURIComponent(shown.party_slug)}?entity=dubaiotc`}
                className="btn-primary text-body py-1.5"
              >
                Open party
              </Link>
            </footer>
          </>
        )}
      </aside>
    </>
  );
}
